/**
 * Time Machine #114 Phase 5 — pure request-body builder for the Lambda's
 * `getEventLogForCrew` method. Drives the History section of
 * `<TimeMachineCrewModal>` in the frontend.
 *
 * Inputs:
 *   candidateId — ObjectId hex string of the Candidates document (24-char hex)
 *   payrollNo   — payroll number string in "1345.01" format (matches
 *                 attribute._kf_payrollNo)
 *   database    — "Warehouses" or "UAT". NOTE (#121): Lambda
 *                  `v05-09-2026-04-QRY01`+ overrides to "Warehouses" for
 *                  all Time Machine read methods regardless of value passed.
 *   since (optional) — ISO timestamp lower bound (inclusive)
 *   until (optional) — ISO timestamp upper bound (inclusive)
 *   limit (optional) — int, default 200, max 1000
 */

const VALID_DATABASES = ["Warehouses", "UAT"];
const OBJECTID_HEX_RE = /^[0-9a-fA-F]{24}$/;

export function buildEventLogForCrewRequestBody({
  candidateId,
  payrollNo,
  database,
  since,
  until,
  limit,
} = {}) {
  if (!VALID_DATABASES.includes(database)) {
    throw new Error(
      `buildEventLogForCrewRequestBody: database must be one of ${VALID_DATABASES.join(", ")} (got ${JSON.stringify(database)})`
    );
  }
  if (typeof candidateId !== "string" || !OBJECTID_HEX_RE.test(candidateId)) {
    throw new Error(
      `buildEventLogForCrewRequestBody: candidateId must be a 24-char hex string (got ${JSON.stringify(candidateId)})`
    );
  }
  if (typeof payrollNo !== "string" || payrollNo.trim() === "") {
    throw new Error(
      `buildEventLogForCrewRequestBody: payrollNo must be a non-empty string (got ${JSON.stringify(payrollNo)})`
    );
  }

  const body = {
    method: "getEventLogForCrew",
    database,
    collection: "AuditSnapshots", // dispatcher artifact
    candidateId,
    payrollNo,
  };

  if (since != null) {
    body.since = toIsoOrThrow(since, "since");
  }
  if (until != null) {
    body.until = toIsoOrThrow(until, "until");
  }
  if (limit != null) {
    if (typeof limit !== "number" || !Number.isFinite(limit) || limit <= 0) {
      throw new Error(
        `buildEventLogForCrewRequestBody: limit must be a positive finite number (got ${JSON.stringify(limit)})`
      );
    }
    body.limit = Math.min(Math.floor(limit), 1000);
  }

  return body;
}

function toIsoOrThrow(value, label) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    throw new Error(
      `buildEventLogForCrewRequestBody: ${label} must be a Date or ISO string (got ${JSON.stringify(value)})`
    );
  }
  return d.toISOString();
}

export const __testables__ = {
  VALID_DATABASES,
  OBJECTID_HEX_RE,
};
